/**
 * AI Clinical Note Generation via Medplum Bot (`ai-clinical-note-generation`).
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier } from '@medplum/fhirtypes';
import {
  ZOOM_TRANSCRIPT_ABSENT_AT_EXT,
  ZOOM_TRANSCRIPT_PURGED_AT_EXT,
  ZOOM_TRANSCRIPT_READY_AT_EXT,
} from '@unify/fhir-model/constants';
import {
  createAiGenerationTraceId,
  createTimingRecorder,
} from './timing/aiNotetakerTiming';

export const AI_NOTE_GENERATION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-clinical-note-generation',
} as const;

export const ZOOM_RECORDING_PROCESSING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'zoom-recording-processing',
} as const;

export type AiNoteGenerationExecuteInput = {
  appointmentId: string;
  providerPrompt?: string;
};

type AiNoteGenerationDisabledResult = {
  disabled: true;
  message?: string;
};

function isAiNoteGenerationDisabledResult(value: unknown): value is AiNoteGenerationDisabledResult {
  return Boolean(value && typeof value === 'object' && (value as AiNoteGenerationDisabledResult).disabled === true);
}

/**
 * Optional caller-supplied timing context so the click, request, and render events
 * all share one traceId rather than splitting across two internally-generated ones.
 */
export type AiNoteGenerationTimingContext = {
  traceId: string;
  aiRunId?: string;
  scenario?: string;
};

export function getAiNoteGenerationBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_IDENTIFIER_SYSTEM?.trim() ??
    AI_NOTE_GENERATION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_IDENTIFIER_VALUE?.trim() ??
    AI_NOTE_GENERATION_BOT_IDENTIFIER.value;
  return { system, value };
}

/** True when Zoom transcript is ready in Zoom and not yet purged by retention cleanup. */
export function isZoomTranscriptReady(appointment: Appointment | undefined): boolean {
  if (!appointment?.extension?.length) return false;
  const ready = appointment.extension.some(
    (e) => e.url === ZOOM_TRANSCRIPT_READY_AT_EXT && Boolean(e.valueDateTime),
  );
  const purged = appointment.extension.some(
    (e) => e.url === ZOOM_TRANSCRIPT_PURGED_AT_EXT && Boolean(e.valueDateTime),
  );
  return ready && !purged;
}

export function isZoomTranscriptPurged(appointment: Appointment | undefined): boolean {
  return (
    appointment?.extension?.some(
      (e) => e.url === ZOOM_TRANSCRIPT_PURGED_AT_EXT && Boolean(e.valueDateTime),
    ) ?? false
  );
}

export function isZoomTranscriptAbsent(appointment: Appointment | undefined): boolean {
  return (
    appointment?.extension?.some(
      (e) => e.url === ZOOM_TRANSCRIPT_ABSENT_AT_EXT && Boolean(e.valueDateTime),
    ) ?? false
  );
}

export type ZoomTranscriptUiState =
  /** Session has not ended — UI should hide the transcript badge. */
  | 'idle'
  | 'processing'
  | 'missing'
  | 'no_recording'
  | 'ready'
  | 'expired';

const TRANSCRIPT_PROCESSING_WINDOW_MS = 60 * 60 * 1000;

/**
 * Derive transcript badge state for the appointment Progress Note / AI Note Taker UI.
 * `idle` means hide the badge (session not ended yet).
 */
export function resolveZoomTranscriptUiState(input: {
  appointment: Appointment | undefined;
  sessionEnded: boolean;
  nowMs?: number;
}): ZoomTranscriptUiState {
  const { appointment, sessionEnded, nowMs = Date.now() } = input;
  if (isZoomTranscriptPurged(appointment)) return 'expired';
  if (isZoomTranscriptReady(appointment)) return 'ready';
  if (!sessionEnded) return 'idle';
  if (isZoomTranscriptAbsent(appointment)) return 'no_recording';

  const endMs = appointment?.end ? Date.parse(appointment.end) : Number.NaN;
  if (Number.isFinite(endMs) && nowMs - endMs > TRANSCRIPT_PROCESSING_WINDOW_MS) {
    return 'missing';
  }
  return 'processing';
}

/**
 * Whether the appointment page should keep asking Zoom whether a transcript exists.
 * True only inside the post-session processing window, where the Zoom webhook may not
 * have arrived yet (or is not wired at all, e.g. local dev without the relay).
 */
export function shouldPollZoomTranscriptStatus(input: {
  appointment: Appointment | undefined;
  sessionEnded: boolean;
  nowMs?: number;
}): boolean {
  return resolveZoomTranscriptUiState(input) === 'processing';
}

export type ZoomTranscriptStatusCheckResult = {
  status: 'ready' | 'processing' | 'no_recording' | 'expired' | 'skipped';
  appointment?: Appointment;
};

/**
 * Non-blocking Zoom existence check (list recordings only). Updates Appointment when
 * transcript is found or confirmed absent. Safe to call after first paint.
 */
export async function requestZoomTranscriptStatusCheck(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<ZoomTranscriptStatusCheckResult> {
  const bots = await medplum.searchResources('Bot', {
    identifier: `${ZOOM_RECORDING_PROCESSING_BOT_IDENTIFIER.system}|${ZOOM_RECORDING_PROCESSING_BOT_IDENTIFIER.value}`,
    _count: '1',
  });
  const botId = bots[0]?.id;
  if (!botId) {
    return { status: 'skipped' };
  }
  const result = await medplum.executeBot(
    botId,
    { action: 'check-transcript-status', appointmentId },
    ContentType.JSON,
  );
  if (!result || typeof result !== 'object') {
    return { status: 'skipped' };
  }
  const status = (result as ZoomTranscriptStatusCheckResult).status;
  const appointment = (result as ZoomTranscriptStatusCheckResult).appointment;
  if (
    status === 'ready' ||
    status === 'processing' ||
    status === 'no_recording' ||
    status === 'expired' ||
    status === 'skipped'
  ) {
    return { status, appointment };
  }
  return { status: 'skipped' };
}

/**
 * Manually trigger AI note generation for a telehealth appointment.
 *
 * Pass `timingContext` (from the caller's existing TimingRecorder) to ensure all
 * timing events — click, request-sent, response-returned, and UI-render — land on
 * the same trace instead of splitting across two internally-generated trace IDs.
 */
export async function requestAiNoteGeneration(
  medplum: MedplumClient,
  appointmentId: string,
  providerPrompt?: string,
  timingContext?: AiNoteGenerationTimingContext,
): Promise<{ traceId: string; aiRunId?: string }> {
  // Use caller-supplied context when provided so all events share one trace.
  const aiRunId = timingContext?.aiRunId ?? `run-${Date.now().toString(36)}`;
  const traceId = timingContext?.traceId ?? createAiGenerationTraceId(appointmentId, undefined, aiRunId);

  // Only create an internal recorder when the caller hasn't supplied one (standalone use).
  const recorder = timingContext
    ? null
    : createTimingRecorder({ traceId, phase: 'ai_generation', appointmentId, aiRunId });

  recorder?.started('ai_generate_request_prepared_at');

  const payload: AiNoteGenerationExecuteInput & { _traceId?: string; _aiRunId?: string; _scenario?: string } = {
    appointmentId,
    ...(providerPrompt?.trim() ? { providerPrompt: providerPrompt.trim() } : {}),
    _traceId: traceId,
    _aiRunId: aiRunId,
    ...(timingContext?.scenario ? { _scenario: timingContext.scenario } : {}),
  };

  const sendStart = recorder?.now() ?? 0;
  recorder?.started('ai_generate_request_sent_at');

  try {
    const result = await medplum.executeBot(
      getAiNoteGenerationBotExecuteTarget(),
      payload,
      ContentType.JSON,
    );
    if (isAiNoteGenerationDisabledResult(result)) {
      throw new Error(result.message ?? 'AI documentation assistance is disabled for this provider or client.');
    }
    recorder?.finished('ai_generate_response_returned_at', sendStart);
  } catch (err) {
    recorder?.failed('ai_generate_request_failed', err);
    throw err;
  }

  return { traceId, aiRunId };
}
